import React, { useState, useEffect, useMemo, useRef } from 'react';
import { rentalApi } from '../../../../api/rentalApi';
import { PDFDownloadLink } from '@react-pdf/renderer';
import RentalPDF from '../pdf/RentalPDF';

/**
 * Rental Dashboard Component.
 * Displays the availability timeline, active rentals list, and upcoming deadlines.
 */
export default function RentalDashboard({ rentals = [], onRefresh, navigateToTab }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    
    // UI State
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [timeRange, setTimeRange] = useState(1);
    const [selectedRental, setSelectedRental] = useState(null);
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [newEndDate, setNewEndDate] = useState('');
    const [confirmReturn, setConfirmReturn] = useState(false);
    
    const timelineRef = useRef(null);
    
    // Search & Sort State
    const [rentalSearch, setRentalSearch] = useState('');
    const [rentalSort, setRentalSort] = useState('end_date');

    useEffect(() => {
        const fetchInventory = async () => {
            try {
                const data = await rentalApi.getItems();
                setItems(data);
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };
        fetchInventory();
    }, []);

    const handleEndRental = async (id) => {
        setActionLoading(true);
        try {
            await rentalApi.updateStatus(id, 'returned');
            onRefresh();
            setSelectedRental(null);
            setConfirmReturn(false);
        } catch (err) { 
            console.error('END_RENTAL_ERROR:', err);
            alert('Fehler beim Beenden der Miete.'); 
        }
        finally { setActionLoading(false); }
    };

    const handleExtendRental = async () => {
        if (!newEndDate) return;
        setActionLoading(true);
        try {
            await rentalApi.updateRental(selectedRental.id, { end_date: newEndDate });
            onRefresh();
            setShowExtendModal(false);
            setSelectedRental(null);
        } catch (err) { 
            console.error('EXTEND_RENTAL_ERROR:', err);
            alert(err.response?.data?.error || 'Verlängerung fehlgeschlagen.'); 
        }
        finally { setActionLoading(false); }
    };

    // Timeline Configuration
    const timelineMeta = useMemo(() => {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        
        const totalDays = timeRange === 1 ? 30 : timeRange * 30;
        const end = new Date(start);
        end.setDate(start.getDate() + totalDays);

        const periods = [];
        for (let i = 0; i < totalDays; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            
            let label = d.getDate().toString();
            let showLabel = true;
            if (timeRange > 1 && d.getDate() !== 1 && d.getDay() !== 1) showLabel = false;
            if (timeRange >= 6 && d.getDate() !== 1) showLabel = false;

            periods.push({
                date: d,
                label: label,
                showLabel: showLabel,
                isWeekend: d.getDay() === 0 || d.getDay() === 6,
                isToday: d.toDateString() === new Date().toDateString()
            });
        }

        return { start, end, totalDays, periods };
    }, [timeRange]);

    const calculateBarStyles = (rentalStart, rentalEnd) => {
        const rStart = new Date(rentalStart);
        const rEnd = new Date(rentalEnd);
        rStart.setHours(0,0,0,0);
        rEnd.setHours(23,59,59,999);

        const { start: timelineStart, totalDays } = timelineMeta;
        
        let offsetDays = (rStart - timelineStart) / (1000 * 60 * 60 * 24);
        let durationDays = (rEnd - rStart) / (1000 * 60 * 60 * 24);

        if (offsetDays < 0) {
            durationDays += offsetDays;
            offsetDays = 0;
        }
        
        const left = (offsetDays / totalDays) * 100;
        const width = (durationDays / totalDays) * 100;

        return {
            left: `${left}%`,
            width: `${Math.max(width, 0.5)}%`,
            display: (left + width < 0 || left > 100) ? 'none' : 'flex'
        };
    };



    const filteredItems = useMemo(() => {
        if (categoryFilter === 'all') return items;
        return items.filter(i => i.category === categoryFilter);
    }, [items, categoryFilter]);

    const sortedActiveRentals = useMemo(() => {
        let filtered = rentals.filter(r => r.status === 'active');
        if (rentalSearch) {
            const lowerSearch = rentalSearch.toLowerCase();
            filtered = filtered.filter(r => 
                r.rental_number?.toLowerCase().includes(lowerSearch) || 
                r.customer_name?.toLowerCase().includes(lowerSearch) ||
                (r.line_items && r.line_items.some(li => li.name?.toLowerCase().includes(lowerSearch)))
            );
        }
        return filtered.sort((a, b) => {
            if (rentalSort === 'end_date') return new Date(a.end_date) - new Date(b.end_date);
            if (rentalSort === 'customer') return (a.customer_name || '').localeCompare(b.customer_name || '');
            if (rentalSort === 'number') return (a.rental_number || '').localeCompare(b.rental_number || '');
            return 0;
        });
    }, [rentals, rentalSearch, rentalSort]);

    const actualReturns = useMemo(() => {
        const today = new Date();
        today.setHours(23,59,59,999);
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);

        return sortedActiveRentals.filter(r => {
            const endDate = new Date(r.end_date);
            return endDate <= nextWeek;
        });
    }, [sortedActiveRentals]);

    if (loading) return <div className="rental-loading">DASHBOARD WIRD GELADEN...</div>;

    const stats = {
        active: rentals.filter(r => r.status === 'active').length,
        overdue: rentals.filter(r => r.status === 'active' && new Date(r.end_date) < new Date()).length,
        due: rentals.filter(r => {
             if (r.status !== 'active') return false;
             const ed = new Date(r.end_date);
             const now = new Date();
             const nextWeek = new Date();
             nextWeek.setDate(now.getDate() + 7);
             return ed >= now && ed <= nextWeek;
        }).length
    };

    return (
        <div className="rental-dashboard-container">
            <style>{`
                .booking-bar-wrapper {
                    position: absolute;
                    top: 6px;
                    height: 24px;
                    z-index: 10;
                }
                .booking-bar-wrapper:hover {
                    z-index: 9999 !important;
                }
                .timeline-tooltip-css {
                    opacity: 0;
                    visibility: hidden;
                    position: absolute;
                    top: 24px;
                    left: 0;
                    padding-top: 6px;
                    transition: opacity 0.2s, visibility 0.2s;
                    transition-delay: 0.1s;
                    pointer-events: none;
                }
                .booking-bar-wrapper:hover .timeline-tooltip-css,
                .timeline-tooltip-css:hover {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto;
                    transition-delay: 0s;
                }
                /* For the last 3 rows, open the tooltip upwards so it doesn't get cut off */
                tbody tr:nth-last-child(-n+3) .timeline-tooltip-css {
                    top: auto;
                    bottom: 24px;
                    padding-top: 0;
                    padding-bottom: 6px;
                }
            `}</style>
            <div className="dashboard-main" style={{ maxWidth: '1400px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '25px' }}>
                    <div className="compact-kpi-card" style={{ background: '#fff', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '140px' }} onClick={() => document.getElementById('active-rentals')?.scrollIntoView({ behavior: 'smooth' })}>
                        <span style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--p-slate-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Laufende Mieten</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: 'var(--optimox-blue)', marginTop: '4px' }}>{stats.active}</span>
                    </div>
                    <div className="compact-kpi-card" style={{ background: '#fff', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '140px' }} onClick={() => document.getElementById('pending-returns')?.scrollIntoView({ behavior: 'smooth' })}>
                        <span style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--p-slate-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Überfällig</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: '#f43f5e', marginTop: '4px' }}>{stats.overdue}</span>
                    </div>
                    <div className="compact-kpi-card" style={{ background: '#fff', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '140px' }}>
                        <span style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--p-slate-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fällig</span>
                        <span style={{ fontSize: '1.4rem', fontWeight: '900', color: '#f59e0b', marginTop: '4px' }}>{stats.due}</span>
                    </div>
                </div>

                <div className="premium-card" style={{ padding: '16px', overflow: 'visible' }}>
                    <div className="timeline-header" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h3 style={{ fontSize: '0.8rem', fontWeight: '900', margin: 0, letterSpacing: '0.05em' }}>VERFÜGBARKEITS-PLAN</h3>
                        </div>
                        <div className="timeline-filters" style={{ display: 'flex', gap: '8px' }}>
                            <select className="filter-select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                                <option value="all">Alle</option>
                                <option value="vehicle">🚜 Fahrzeuge</option>
                                <option value="material">📦 Material</option>
                                <option value="other">🛠️ Sonstiges</option>
                            </select>
                            <div className="rental-tabs-inline" style={{ background: 'var(--p-slate-100)', padding: '2px' }}>
                                {[1, 3, 6, 12].map(range => (
                                    <button 
                                        key={range}
                                        className={`tab-inline ${timeRange === range ? 'active' : ''}`}
                                        style={{ padding: '4px 10px', fontSize: '0.65rem' }}
                                        onClick={() => setTimeRange(range)}
                                    >
                                        {range}M
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="availability-grid-container" ref={timelineRef} style={{ overflowX: 'auto' }}>
                        <table className="rental-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '140px', textAlign: 'left', padding: '8px 12px', fontSize: '0.65rem', background: '#fff', borderBottom: '1px solid var(--p-slate-100)', position: 'sticky', left: 0, zIndex: 30 }}>OBJEKT</th>
                                    {timelineMeta.periods.map((p, i) => (
                                        <th key={i} style={{ 
                                            background: p.isToday ? 'var(--p-blue-50)' : (p.isWeekend ? 'var(--p-slate-50)' : '#fff'),
                                            borderBottom: '1px solid var(--p-slate-100)',
                                            borderLeft: '1px solid var(--p-slate-50)',
                                            padding: '8px 0',
                                            fontSize: '0.6rem',
                                            fontWeight: '800',
                                            color: p.isToday ? 'var(--optimox-blue)' : '#94a3b8'
                                        }}>
                                            {p.showLabel ? p.label : ''}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map(item => (
                                    <tr key={item.id} style={{ height: '40px' }}>
                                        <td style={{ fontSize: '0.7rem', fontWeight: '800', padding: '0 12px', position: 'sticky', left: 0, background: '#fff', zIndex: 25, borderBottom: '1px solid var(--p-slate-50)', borderRight: '1px solid var(--p-slate-100)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {item.name}
                                        </td>
                                        <td colSpan={timelineMeta.totalDays} style={{ position: 'relative', padding: 0, borderBottom: '1px solid var(--p-slate-50)' }}>
                                            {rentals.filter(r => r.status === 'active' && r.line_items?.some(li => li.rental_item_id === item.id)).map(r => {
                                                const styles = calculateBarStyles(r.start_date, r.end_date);
                                                const isOverdue = new Date(r.end_date) < new Date();
                                                const isDueSoon = new Date(r.end_date) >= new Date() && new Date(r.end_date) <= new Date(new Date().setDate(new Date().getDate() + 3));
                                                
                                                return (
                                                    <div 
                                                        key={r.id}
                                                        className="booking-bar-wrapper"
                                                        style={{ ...styles }}
                                                    >
                                                        <div 
                                                            className={`booking-bar ${isOverdue ? 'overdue' : ''}`}
                                                            style={{ 
                                                                width: '100%',
                                                                height: '100%',
                                                                cursor: 'pointer',
                                                                background: isOverdue ? '#f43f5e' : 'var(--optimox-blue)',
                                                                color: 'white',
                                                                borderRadius: '6px',
                                                                padding: '0 8px',
                                                                fontSize: '0.65rem',
                                                                fontWeight: '800',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                overflow: 'hidden'
                                                            }}
                                                            onClick={() => setSelectedRental(r)}
                                                        >
                                                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                                                {timeRange <= 3 && r.rental_number}
                                                            </span>
                                                            {(isOverdue || isDueSoon) && timeRange <= 3 && (
                                                                <span style={{ marginLeft: '6px', background: 'rgba(255,255,255,0.25)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.55rem', display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                                                                    <span style={{ fontWeight: '900', color: '#fff' }}>!</span> Abgabe
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Pure CSS Tooltip */}
                                                        <div className="timeline-tooltip-css">
                                                            <div 
                                                                style={{ 
                                                                    cursor: 'pointer',
                                                                    background: '#ffffff',
                                                                    color: 'var(--p-slate-900)',
                                                                    border: '1px solid var(--p-slate-200)',
                                                                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                                                                    padding: '12px 16px',
                                                                    borderRadius: '12px',
                                                                    minWidth: '240px',
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedRental(r);
                                                                }}
                                                            >
                                                                <div style={{ fontSize: '0.65rem', color: 'var(--p-slate-500)', fontWeight: '800', marginBottom: '4px', textTransform: 'uppercase' }}>OBJEKT: {item.name}</div>
                                                                <strong style={{ display: 'block', fontSize: '0.9rem', color: 'var(--optimox-blue)', marginBottom: '8px', whiteSpace: 'normal' }}>{r.customer_name}</strong>
                                                                <div className="date-info" style={{ color: 'var(--p-slate-600)', fontSize: '0.7rem', fontWeight: '600', marginBottom: '8px' }}>
                                                                    📅 {new Date(r.start_date).toLocaleDateString('de-DE')} - {new Date(r.end_date).toLocaleDateString('de-DE')}
                                                                </div>
                                                                <div style={{ fontSize: '0.7rem', color: 'var(--p-slate-800)', fontWeight: '800', display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                                    <span>NR: {r.rental_number}</span>
                                                                    <span>{parseFloat(r.total_brutto).toFixed(2)} €</span>
                                                                </div>
                                                                <div style={{ borderTop: '1px solid var(--p-slate-100)', paddingTop: '8px', textAlign: 'center', fontSize: '0.65rem', fontWeight: '800', color: 'var(--optimox-blue)' }}>
                                                                    Klicken für Details & Verwaltung
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div style={{ display: 'flex', position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                                                {timelineMeta.periods.map((p, i) => (
                                                    <div key={i} style={{ 
                                                        flex: 1, 
                                                        borderRight: '1px solid rgba(0,0,0,0.02)',
                                                        background: p.isToday ? 'rgba(14, 165, 233, 0.05)' : 'transparent' 
                                                    }} />
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div id="active-rentals" className="premium-card" style={{ marginTop: '20px', padding: '0' }}>
                    <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--p-slate-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '0.85rem', fontWeight: '900', color: 'var(--p-slate-800)' }}>AKTIVE VERMIETUNGEN</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <input 
                                type="text" 
                                placeholder="Suchen..." 
                                value={rentalSearch}
                                onChange={e => setRentalSearch(e.target.value)}
                                style={{ padding: '6px 12px', fontSize: '0.75rem', borderRadius: '8px', border: '1px solid var(--p-slate-200)', outline: 'none' }}
                            />
                            <select 
                                value={rentalSort} 
                                onChange={e => setRentalSort(e.target.value)}
                                style={{ padding: '6px 12px', fontSize: '0.75rem', borderRadius: '8px', border: '1px solid var(--p-slate-200)', outline: 'none', background: '#fff', cursor: 'pointer' }}
                            >
                                <option value="end_date">Nach Zeitraum</option>
                                <option value="customer">Nach Kunde</option>
                                <option value="number">Nach Miete (Nr)</option>
                            </select>
                        </div>
                    </div>
                    <div className="modern-table-wrapper" style={{ padding: '0 10px 10px 10px' }}>
                        <table className="premium-style-table" style={{ marginTop: '10px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '80px', textTransform: 'none', color: 'var(--p-slate-500)' }}>Nummer</th>
                                    <th style={{ textTransform: 'none', color: 'var(--p-slate-500)' }}>Miete</th>
                                    <th style={{ textTransform: 'none', color: 'var(--p-slate-500)' }}>Kunde</th>
                                    <th style={{ textTransform: 'none', color: 'var(--p-slate-500)' }}>Zeitraum</th>
                                    <th style={{ textAlign: 'right', textTransform: 'none', color: 'var(--p-slate-500)' }}>Verwalten</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedActiveRentals.map(r => (
                                    <tr key={r.id} style={{ borderBottom: '1px solid var(--p-slate-50)', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ fontWeight: '800', color: 'var(--optimox-blue)' }}>{r.rental_number}</td>
                                        <td style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--p-slate-700)' }}>
                                            {r.line_items && r.line_items.length > 0 ? r.line_items.map(li => li.name).join(', ') : 'Verschiedenes'}
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: '700', fontSize: '0.8rem', color: 'var(--p-slate-800)' }}>{r.customer_name}</div>
                                            <div style={{ fontSize: '0.65rem', color: 'var(--p-slate-400)' }}>{r.location || 'Ohne Einsatzort'}</div>
                                        </td>
                                        <td style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                            {new Date(r.start_date).toLocaleDateString('de-DE')} 
                                            <span style={{ margin: '0 6px', color: 'var(--p-slate-300)' }}>→</span> 
                                            <span style={{ color: new Date(r.end_date) < new Date() ? '#f43f5e' : 'var(--p-slate-700)' }}>
                                                {new Date(r.end_date).toLocaleDateString('de-DE')}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button 
                                                className="rental-btn-secondary" 
                                                style={{ padding: '6px 12px', fontSize: '0.65rem', borderRadius: '6px', border: '1px solid var(--p-slate-200)', background: '#fff', cursor: 'pointer', fontWeight: '700', color: 'var(--p-slate-600)' }} 
                                                onClick={() => setSelectedRental(r)}
                                                onMouseEnter={e => { e.target.style.background = 'var(--p-slate-50)'; e.target.style.borderColor = 'var(--p-slate-300)'; }}
                                                onMouseLeave={e => { e.target.style.background = '#fff'; e.target.style.borderColor = 'var(--p-slate-200)'; }}
                                            >
                                                Verwalten
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <aside className="deadline-sidebar premium-card" id="pending-returns" style={{ padding: '16px' }}>
                <div className="sidebar-heading" style={{ fontSize: '0.7rem', fontWeight: '900', color: 'var(--p-slate-900)', marginBottom: '16px', letterSpacing: '0.05em' }}>
                    📦 ANSTEHENDE RÜCKGABEN
                </div>
                <div className="deadline-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {actualReturns.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--p-slate-300)', fontSize: '0.7rem' }}>Keine Rückgaben in den nächsten 7 Tagen</div>
                    ) : (
                        actualReturns.map(r => (
                            <div key={r.id} className="deadline-card highlight-on-hover" style={{ 
                                padding: '12px', 
                                background: 'var(--p-slate-50)', 
                                borderRadius: '12px', 
                                borderLeft: `4px solid ${new Date(r.end_date) < new Date() ? '#f43f5e' : '#f59e0b'}`,
                                cursor: 'pointer'
                             }} onClick={() => setSelectedRental(r)}>
                                <div style={{ fontSize: '0.65rem', fontWeight: '800', color: new Date(r.end_date) < new Date() ? '#f43f5e' : '#f59e0b', marginBottom: '4px' }}>
                                    {new Date(r.end_date) < new Date() ? '🚨 ÜBERFÄLLIG' : `Fällig am ${new Date(r.end_date).toLocaleDateString('de-DE')}`}
                                </div>
                                <div style={{ fontSize: '0.8rem', fontWeight: '800' }}>{r.rental_number}</div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--p-slate-700)', fontWeight: '600' }}>{r.customer_name}</div>
                            </div>
                        ))
                    )}
                </div>
            </aside>

            {selectedRental && (
                <div className="action-modal-overlay" onClick={() => { setSelectedRental(null); setConfirmReturn(false); }}>
                    <div className="action-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '900' }}>#{selectedRental.rental_number}</h3>
                            <button onClick={() => setSelectedRental(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--p-slate-300)' }}>✕</button>
                        </div>
                        
                        <div className="action-grid">
                            {!confirmReturn ? (
                                <>
                                    <button className="action-item-btn" onClick={() => setConfirmReturn(true)}>
                                        📦 Erhalt bestätigen (Beenden)
                                    </button>
                                    <button className="action-item-btn" onClick={() => { setShowExtendModal(true); setNewEndDate(selectedRental.end_date); }}>
                                        ⏳ Mietzeitraum verlängern
                                    </button>
                                    <button className="action-item-btn" onClick={() => window.location.href=`mailto:${selectedRental.customer_email}`}>
                                        📧 Mieter kontaktieren
                                    </button>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center' }}>
                                    <p style={{ fontSize: '0.8rem', fontWeight: '700', marginBottom: '16px' }}>Rückgabe wirklich bestätigen?</p>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button className="rental-btn-primary" style={{ flex: 1, background: '#10b981' }} onClick={() => handleEndRental(selectedRental.id)} disabled={actionLoading}>JA, ERHALTEN</button>
                                        <button className="rental-btn-secondary" style={{ flex: 1 }} onClick={() => setConfirmReturn(false)}>NEIN</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showExtendModal && (
                <div className="action-modal-overlay">
                    <div className="action-modal" style={{ width: '420px' }}>
                        <h3 style={{ fontSize: '0.85rem', fontWeight: '900', marginBottom: '20px' }}>VERLÄNGERUNG & RECHNUNG</h3>
                        
                        <div className="form-group-compact" style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--p-slate-400)' }}>NEUES RÜCKGABEDATUM</label>
                            <input 
                                type="date" 
                                value={newEndDate ? new Date(newEndDate).toISOString().split('T')[0] : ''} 
                                onChange={e => setNewEndDate(e.target.value)} 
                                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid var(--p-slate-100)', marginTop: '8px', fontSize: '0.85rem' }} 
                            />
                        </div>

                        <div className="form-group-compact" style={{ marginBottom: '24px' }}>
                            <label style={{ fontSize: '0.65rem', fontWeight: '800', color: 'var(--p-slate-400)' }}>KAUFMÄNNISCHE ABWICKLUNG</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', padding: '8px', background: 'var(--p-slate-50)', borderRadius: '8px', cursor: 'pointer' }}>
                                    <input type="radio" name="billing" defaultChecked /> Bestehenden Vorgang aktualisieren
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', padding: '8px', background: 'var(--p-slate-50)', borderRadius: '8px', cursor: 'pointer' }}>
                                    <input type="radio" name="billing" /> Zusatzrechnung erstellen
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', padding: '8px', background: 'var(--p-slate-50)', borderRadius: '8px', cursor: 'pointer' }}>
                                    <input type="radio" name="billing" /> Komplette neue Rechnung erzeugen
                                </label>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="rental-btn-primary" style={{ flex: 1 }} onClick={handleExtendRental} disabled={actionLoading}>SPEICHERN & BERECHNEN</button>
                            <button className="rental-btn-secondary" style={{ flex: 1 }} onClick={() => setShowExtendModal(false)}>ABBRECHEN</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
